import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FieldArray } from '~src/settings/FormikWrapperForStubbing';
import { cssClass } from '~src/style';
import { IRemoteAgentConnectionStatus } from '~src/ts/ServerApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';

import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { UserDefinedPoolModel } from '../types/PoolModel';
import { createPoolOptionsFromPools, createPoolOptionsFromSystemDefinedPools } from '../utils/PoolModelUtilities';
import { BridgeMultiPoolTableEntryAgents } from './BridgeMultiPoolTableEntryAgents';
import { BridgeMultiPoolTableEntryPool } from './BridgeMultiPoolTableEntryPool';
import { ExpandPoolButton } from './ExpandPoolButton';
import { LoadingPoolsSpinner } from './LoadingPoolsSpinner';

export type BridgeMultiPoolTableBodyProps = Readonly<{
  pools: Record<string, UserDefinedPoolModel>;
  existingPoolNames: ReadonlyArray<string>;
  connectionStatuses: IRemoteAgentConnectionStatus[];
  siteId: string;
  refreshEdgePools: () => void;
  hasLoadedEdgePools: boolean;
}>;

export function BridgeMultiPoolTableBody(props: BridgeMultiPoolTableBodyProps) {
  const [expandedRowIndex, setExpandedRowIndex] = React.useState(-1);
  const poolOptions: ReadonlyArray<PoolAssignmentOption> = React.useMemo(() => {
    let items: PoolAssignmentOption[] = createPoolOptionsFromPools(props.pools);
    items = items.concat(createPoolOptionsFromSystemDefinedPools());
    return items;
  }, [props.pools]);
  const pools: ReadonlyArray<UserDefinedPoolModel> = Object.values(props.pools);
  const isDisabled = (pool: UserDefinedPoolModel) => {
    return Object.values(pool.agents).length === 0;
  };

  if(!props.hasLoadedEdgePools) {
    return LoadingPoolsSpinner();
  }

  if (pools.length === 0) {
    return renderNoPoolsWarning();
  }

  return (
    <FieldArray
      name={''}
      render={() => (
        pools.map((pool: UserDefinedPoolModel, index) => (
          <tbody key={index}>
            <tr>
              <ExpandPoolButton
                isExpanded={expandedRowIndex === index && !isDisabled(pool)}
                setExpanded={(expand: boolean) => setExpandedRowIndex(expand ? index : -1)}
                isDisabled={isDisabled(pool)}
              />
              <BridgeMultiPoolTableEntryPool
                pool={pool}
                existingPoolNames={props.existingPoolNames}
                isExpanded={expandedRowIndex === index && !isDisabled(pool)}
                connectionStatuses={props.connectionStatuses}
                siteId={props.siteId}
                refreshEdgePools={props.refreshEdgePools}
              />
            </tr>
            {(expandedRowIndex === index && !isDisabled(pool)) &&
              <tr className={focusClass}>
                <td className={'tb-data-grid-separator-row'} />
                <td colSpan={5}>
                  <BridgeMultiPoolTableEntryAgents
                    agents={Object.values(pool.agents)}
                    connectionStatuses={props.connectionStatuses}
                    poolOptions={poolOptions}
                    siteId={props.siteId}
                    agentPoolId={pool.id}
                    refreshEdgePools={props.refreshEdgePools}
                  />
                </td>
              </tr>}
          </tbody>))
      )}
    />
  );
}

function renderNoPoolsWarning() {
  return (
    <tbody>
      <tr>
        <td colSpan={9}>
          <div className={'tb-data-grid-cell-text tb-data-grid-bridge-center'}>
            {Messages.siteRemoteAgentSettingsPoolNotFoundWarning()}
          </div>
        </td>
      </tr>
    </tbody>);
}

const focusClass = cssClass('MultiPool', 'expanded-focus', {
  background: 'rgba(255, 255, 255, 0.00)',
  border: '1px solid #3498DB',
  boxShadow: '0 0 4px 0 #3498DB',
  borderRadius: '1px'
});
